import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import type { FollowUpQuery } from '../api/endpoints';
import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { listDue } from './nextCall';

const TODAY: FollowUpQuery = { assigned: 'me', status: 'open', due: 'today' };
const SHOWN = 8;

// TodayFollowUps: Rückrufe und Wiedervorlagen von heute (inklusive überfälliger), die mir oder niemandem zugewiesen
// sind – mit Uhrzeit, damit feste Rückruftermine eingehalten werden. Telefonisten rufen direkt an, der Support
// öffnet den Lead oder hakt ab.
export function TodayFollowUps({ callable, allTo }: { callable: boolean; allTo: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const base = `/p/${project.key}`;
  const list = useQuery({
    queryKey: projectKeys.followUps(project.id, TODAY),
    queryFn: ({ signal }) => papi.followUps(TODAY, signal),
    refetchInterval: 60_000,
  });
  const complete = useMutation({
    mutationFn: (id: string) => papi.completeFollowUp(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'followups'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'queue-peek'] });
      void queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(project.id) });
    },
  });
  const items = list.data?.items ?? [];
  const overdue = items.filter((i) => i.overdue).length;

  return (
    <section className="card today-followups" aria-labelledby="today-followups-title">
      <div className="section-head">
        <h2 id="today-followups-title">
          Wiedervorlagen heute {list.data && <span className="badge badge-muted">{items.length}</span>}
        </h2>
        {overdue > 0 && <span className="badge badge-error">{overdue} überfällig</span>}
      </div>
      {list.isPending ? (
        <p className="muted">Wird geladen …</p>
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <p className="muted">Heute stehen keine Rückrufe oder Wiedervorlagen an.</p>
      ) : (
        <ul className="today-list">
          {items.slice(0, SHOWN).map((f) => (
            <li key={f.id} className={f.overdue ? 'today-item overdue' : 'today-item'}>
              <span className="today-time">{listDue(f.due_at)}</span>
              <span className={f.type === 'CALLBACK' ? 'badge badge-warn' : 'badge badge-muted'}>{f.type === 'CALLBACK' ? 'Rückruf' : 'Wiedervorlage'}</span>
              <span className="today-lead">
                <Link to={`${base}/leads/${f.lead_id}`}>{f.lead_name}</Link>
                {f.lead_city ? ` · ${f.lead_city}` : ''}
                {(f.title || f.reason) && <small>{f.reason || f.title}</small>}
              </span>
              <span className="today-actions">
                {callable ? (
                  <Link className="btn btn-small" to={`${base}/akquise/${f.lead_id}`} aria-label={`${f.lead_name} anrufen`}>
                    Anrufen
                  </Link>
                ) : (
                  <button type="button" className="btn btn-small" disabled={complete.isPending} onClick={() => complete.mutate(f.id)}>
                    Erledigt
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {complete.isError && <MutationError error={complete.error} />}
      <p className="today-more">
        <Link to={allTo}>{items.length > SHOWN ? `Alle ${items.length} Wiedervorlagen →` : 'Alle Wiedervorlagen →'}</Link>
      </p>
    </section>
  );
}
